import {useCallback, Reducer, useReducer, Dispatch, useRef} from 'react';

// Define a thunk type that matches Redux thunk pattern
export type Thunk<S, A> = (dispatch: Dispatch<A | Thunk<S, A>>, getState: () => S) => Promise<void> | void;

/**
 * A hook that extends React's useReducer hook to support Redux-style thunks.
 *
 * A thunk can be used to dispatch actions that are not synchronous or have complex logic.
 * See https://redux.js.org/usage/writing-logic-thunks for more information.
 *
 * Note that this is only inspired by Redux's thunk middleware.
 * Redux is not used with this hook nor is it a direct port.
 *
 * @param reducer - The reducer function to use.
 * @param initialState - The initial state of the reducer.
 * @returns A tuple containing the current state and the dispatch function.
 */
export default function useReducerWithThunk<S, A>(
  reducer: Reducer<S, A>,
  initialState: S
): [S, Dispatch<A | Thunk<S, A>>] {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Use ref to always have access to current state, avoiding stale closure
  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatchWithThunk = useCallback(
    (action: A | Thunk<S, A>) => {
      if (typeof action === 'function') {
        // Pass dispatchWithThunk to thunks so they can dispatch other thunks if needed
        // Use stateRef.current to get the latest state, avoiding stale closure
        (action as Thunk<S, A>)(dispatchWithThunk, () => stateRef.current);
      } else {
        dispatch(action);
      }
    },
    [dispatch]
  );
  return [state, dispatchWithThunk];
}
